import React, { SetStateAction } from "react";
import IntegrationType from "types/enums/integrationType";
import Role from "types/enums/role";
import SubscriptionType from "types/enums/subscriptionType";
import { UserInsert } from "types/userInsert";
import { Constants } from "utils/constants/constants";
import * as Yup from "yup";

export const validationSchema = Yup.object({
  companyName: Yup.string().required("Company name is required"),
  companyWebsite: Yup.string()
    .transform((value) => {
      if (!value) return value;
      // Add https:// if no protocol is present
      if (!value.startsWith("http://") && !value.startsWith("https://")) {
        return `https://${value}`;
      }
      return value;
    })
    .test("is-valid-website", "Invalid website", (value) => {
      if (!value) return false;
      const regex = Constants.websiteRegex;
      return regex.test(value);
    }),
  companyLinkedinUrl: Yup.string()
    .nullable()
    .test("is-valid-linkedin-url", "Invalid Linkedin URL", (value) => {
      if (!value) return true;
      const regex = Constants.linkedinUrlRegex;
      return regex.test(value);
    }),
  licenses: Yup.number()
    .min(1, "Must be at least 1 user")
    .max(
      Constants.maxBasicUserQuantity,
      "Must be at most " + Constants.maxBasicUserQuantity + " users"
    )
    .required("Number of licenses is required"),
  managerLicenses: Yup.number()
    .min(1, "Must be at least 1 manager")
    .max(
      Constants.maxManagerUserQuantity,
      "Must be at most " + Constants.maxManagerUserQuantity + " managers"
    )
    .required("Manager user quantity is required"),
  subscriptionType: Yup.string()
    .oneOf(
      [
        SubscriptionType.STARTER,
        SubscriptionType.PRO,
        SubscriptionType.ULTRA,
        SubscriptionType.FREE_TRIAL,
      ],
      "Invalid Subscription type"
    )
    .required("Subscription type is required"),
  integrationType: Yup.string()
    .oneOf(
      ["hubspot", "salesforce", "crono", "pipedrive"],
      "Invalid integration type"
    )
    .required("Integration type is required"),
  expirationDate: Yup.date()
    .min(
      new Date(Date.now() + 1000 * 60 * 60 * 24 * 6),
      "Must be at least 7 days from now"
    )
    .max(
      new Date(Date.now() + 1000 * 60 * 60 * 24 * 365 * 6),
      "Must be at most 6 years from now"
    )
    .required("Expiration date is required"),
  // subscriptionType: Yup.string().required("Subscription type is required"),
  // otherSettings
  currency: Yup.string()
    .oneOf(["$", "€"], "Invalid currency")
    .required("Currency is required"),
  hasLinkedin: Yup.boolean(),
  hasEmail: Yup.boolean(),
  hasCall: Yup.boolean(),
  hasLastFeatures: Yup.boolean(),
  hasLateSync: Yup.boolean(),
  monthScrapeRequest: Yup.number()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === "" ? null : value
    )
    .max(
      Constants.maxMonthScrapeRequest,
      "Must be at most " +
        Constants.maxMonthScrapeRequest.toString() +
        " requests"
    ),
  monthVerifyRequest: Yup.number()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === "" ? null : value
    )
    .max(
      Constants.maxMonthVerifyRequest,
      "Must be at most " +
        Constants.maxMonthVerifyRequest.toString() +
        " requests"
    ),
  monthFindPhoneRequest: Yup.number()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === "" ? null : value
    )
    .max(
      Constants.maxMonthScrapeRequest,
      "Must be at most " +
        Constants.maxMonthScrapeRequest.toString() +
        " requests"
    ),
  monthGenerateTemplateRequest: Yup.number()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === "" ? null : value
    )
    .max(
      Constants.maxMonthGenerateTemplateRequest,
      "Must be at most " +
        Constants.maxMonthGenerateTemplateRequest.toString() +
        " requests"
    ),
  // string list
  subscriptionDomains: Yup.array()
    .of(Yup.string().matches(Constants.domainRegex))
    .nullable(),
  priceId: Yup.string().nullable(),
  coupon: Yup.string().nullable(),
});

export interface SubscriptionInputs {
  companyName: string;
  companyWebsite: string | null;
  companyLinkedinUrl: string | null;
  licenses: number;
  managerLicenses: number;
  subscriptionType: SubscriptionType;
  integrationType: IntegrationType;
  expirationDate: Date;
  currency: string;
  hasLinkedin: boolean;
  hasEmail: boolean;
  hasCall: boolean;
  hasLateSync: boolean;
  monthScrapeRequest: number | null;
  monthVerifyRequest: number | null;
  monthFindPhoneRequest: number | null;
  hasLastFeatures: boolean;
  monthGenerateTemplateRequest: number | null;
  users: UserInsert[];
  multiCustomer: boolean;
  subscriptionDomains: string[] | null;
  priceId: string | null;
  coupon: string | null;
}

export const initialValues = {
  companyName: "",
  companyWebsite: null,
  companyLinkedinUrl: null,
  licenses: 1,
  managerLicenses: 1,
  subscriptionType: SubscriptionType.STARTER,
  integrationType: IntegrationType.CRONO,
  expirationDate: new Date(),
  currency: "€",
  hasLinkedin: true,
  hasEmail: true,
  hasCall: true,
  hasLateSync: false,
  monthScrapeRequest: null,
  monthVerifyRequest: null,
  monthFindPhoneRequest: null,
  hasLastFeatures: false,
  monthGenerateTemplateRequest: null,
  multiCustomer: false,
  subscriptionDomains: null,
  priceId: null,
  coupon: null,
  users: [],
};

export const userInitialValues = {
  email: "",
  firstName: "",
  lastName: "",
  userRoles: ["Basic"],
};

export const userValidationSchema = Yup.object({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  userRoles: Yup.array()
    .min(1)
    .of(Yup.string().oneOf(Object.values(Role), "Invalid role")),
});

export interface InsertSubscriptionProps {
  setOpen: React.Dispatch<SetStateAction<boolean>>;
}
